import React from 'react';
import I18n from 'i18n-js';
import classNames from 'classnames';

import ConditionDisplay from './condition_display';
import PriceWithShipping from './price_with_shipping';
import SellerInfo from './seller_info';
import AffirmEstimateListingCardCTA from './affirm_estimate_listing_card_cta';
import SignupSigninModal from './signup_signin_modal';
import CoreLink from './core_link';
import {
  Listing,
  core_apimessages_Listing_ListingType,
} from '../rql';
import AddToCartButton from './add_to_cart_button';
import CreateOfferButton from '../offers/create_offer_button';
import WatchBadge from './watch_badge';
import {
  withUserContext,
  IUserContext,
} from './user_context_provider';
import { listingItemPagePath } from '../url_helpers';
import { buildListingClickEvent } from '../elog/mparticle_tracker';
import BumpTag, { shouldShowBumpTag } from './bump_tag';
import { WEB } from '../constants';
import { ListingOfferMetadata } from './listing_offer_metadata';
import LazyImage from './lazy_image';
import { useUser } from '../user_hooks';

const DIGITAL_LISTING_UUID = 'e71b6e43-7217-4746-b4e3-64bf708a2664';

interface IProps extends IUserContext {
  listing?: Listing;
  position?: number;
  inMobileApp?: boolean;
  hideMetadataAsOffer?: boolean;
  onOfferSuccess?: Function;
  listingsCount?: number;
  showEstimatedMonthlyPayment?: boolean;
  componentName?: string;
}

interface IState {
  modalOpen: boolean;
}

function PlaceholderCard() {
  return (
    <div className="listing-row-card listing-row-card--placeholder">
      <div className="listing-row-card__inner">
        <div className="listing-row-card__main">
          <div className="listing-row-card__info-and-image">
            <div className="listing-row-card__image">
              <div className="listing-row-card__image__inner" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CSPListingRowCardExperiment(props: IProps) {
  const [modalOpen, setModalOpen] = React.useState(false);
  const user = useUser();

  const openNewTab = user && user.deviceName === WEB;
  const { listing } = props;

  if (!props.listing) { return <PlaceholderCard />; }

  const classes = classNames(
    'listing-row-card',
    'listing-row-card--layout-experiment',
    { 'listing-row-card--software': listing.categoryUuids.includes(DIGITAL_LISTING_UUID) },
    { 'listing-row-card--discounted': !!listing.pricing.originalPrice },
  );

  const clickEvent = buildListingClickEvent({
    listing,
    position: props.position,
    listingsCount: props.listingsCount,
    componentName: props.componentName,
  });

  return (
    <div className={classes}>
      <div className="listing-row-card__inner">
        {!props.hideMetadataAsOffer && <ListingOfferMetadata listing={listing} />}
        <div className="listing-row-card__main">
          <div className="listing-row-card__image-container">
            <div className="listing-row-card__image">
              <CoreLink
                to={listingItemPagePath(listing)}
                target={openNewTab ? '_blank' : undefined}
                clickEvent={clickEvent}
              >
                <LazyImage
                  src={listing.images[0].source}
                />
              </CoreLink>
            </div>
            <div className="listing-row-card__shop-info--mobile">
              <SellerInfo listing={listing} hideBadges />
              {shouldShowBumpTag(listing) &&
                <div className="listing-row-card__bump-tag">
                  {I18n.t('commons.bumpsView.header')}
                </div>
              }
            </div>
          </div>
          <div className="listing-row-card__info-container">
            <div className="listing-row-card__title-and-price">
              <div>
                <CoreLink
                  to={listingItemPagePath(listing)}
                  className="listing-row-card__title"
                  target={openNewTab ? '_blank' : undefined}
                  clickEvent={clickEvent}
                >
                  {listing.title}
                </CoreLink>
                <div className="listing-row-card__condition">
                  <ConditionDisplay
                    slug={listing.condition.conditionSlug}
                    displayName={listing.condition.displayName}
                    textOnly
                  />
                </div>
              </div>
              <div className="listing-row-card__pricing">
                <div className="listing-row-card__pricing__amount">
                  <PriceWithShipping listing={listing} />
                  {props.showEstimatedMonthlyPayment &&
                    <div className="mb-half">
                      <AffirmEstimateListingCardCTA listing={listing} />
                    </div>
                  }
                </div>
              </div>
            </div>
            <div className="listing-row-card__shop-and-actions">
              <div className="listing-row-card__shop-info">
                <SellerInfo listing={listing} hideBadges />
                {shouldShowBumpTag(listing) &&
                  <div className="listing-row-card__bump-tag">
                    {I18n.t('commons.bumpsView.header')}
                  </div>
                }
              </div>
              <div className="listing-row-card__pricing__actions">
                <WatchBadge
                  listing={listing}
                  position={props.position}
                  parentComponentName={props.componentName}
                  displayStyle="text"
                  handleLoggedOutUser={() => setModalOpen(true)}
                />
                { listing.listingType === core_apimessages_Listing_ListingType.MAKE_AN_OFFER &&
                  <CreateOfferButton
                    listingId={listing.id}
                    listing={listing}
                    onOfferSuccess={props.onOfferSuccess}
                    position={props.position}
                    parentComponentName={props.componentName}
                    bumpKey={listing.bumpKey}
                  />
                }
                <AddToCartButton
                  listing={listing}
                  className="listing-row-card__pricing__actions"
                  position={props.position}
                  parentComponentName={props.componentName}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <SignupSigninModal
        onRequestClose={() => setModalOpen(false)}
        active={modalOpen}
        trackSource="WatchBadge"
      />
    </div>
  );
}

export class ListingRowCard extends React.Component<IProps, IState> {
  state = {
    modalOpen: false,
  };

  isDigitalListing() {
    if (!this.props.listing.categoryUuids) { return false; }

    return this.props.listing.categoryUuids.includes(DIGITAL_LISTING_UUID);
  }

  renderConditionDisplay() {
    if (this.isDigitalListing()) return null;

    return (
      <div className="listing-row-card__condition">
        <ConditionDisplay
          slug={this.props.listing.condition.conditionSlug}
          displayName={this.props.listing.condition.displayName}
        />
      </div>
    );
  }

  hasDiscount() {
    return !!this.props.listing.pricing.originalPrice;
  }

  renderMetadata() {
    if (this.props.hideMetadataAsOffer) return null;

    return (
      <ListingOfferMetadata listing={this.props.listing} />
    );
  }

  acceptsOffers() {
    const { listing } = this.props;

    return listing.listingType === core_apimessages_Listing_ListingType.MAKE_AN_OFFER;
  }

  renderOfferButton() {
    const { listing } = this.props;

    if (!this.acceptsOffers()) { return null; }

    return (
      <CreateOfferButton
        listingId={listing.id}
        listing={listing}
        onOfferSuccess={this.props.onOfferSuccess}
        position={this.props.position}
        parentComponentName={this.props.componentName}
        bumpKey={listing.bumpKey}
      />
    );
  }

  renderAffirmCallout() {
    const { listing, showEstimatedMonthlyPayment } = this.props;

    if (!showEstimatedMonthlyPayment) { return null; }

    return (
      <div className="mb-half">
        <AffirmEstimateListingCardCTA listing={listing} />
      </div>
    );
  }

  renderInner() {
    return (
      <>
        {this.renderMetadata()}
        <div className="listing-row-card__main">
          <div className="listing-row-card__info-and-image">
            <div
              className="listing-row-card__image"
            >
              <LazyImage
                src={this.props.listing.images[0].source}
              />
            </div>
            <div className="listing-row-card__info">
              {shouldShowBumpTag(this.props.listing) &&
                <div className="listing-row-card__bump-tag">
                  <BumpTag />
                </div>
              }
              <h4 className="listing-row-card__title">
                {this.props.listing.title}
              </h4>
              <SellerInfo
                listing={this.props.listing}
              />
            </div>
          </div>
          <div className="listing-row-card__pricing">
            <div className="listing-row-card__pricing__amount">
              <PriceWithShipping listing={this.props.listing} />
              {this.renderAffirmCallout()}
              {this.renderConditionDisplay()}
            </div>
            <div className="ml-2 align-center">
              <AddToCartButton
                listing={this.props.listing}
                className="listing-row-card__pricing__amount"
                position={this.props.position}
                parentComponentName={this.props.componentName}
              />
              <div className="mt-half">
                {this.renderOfferButton()}
              </div>
              <div className="mt-1">
                <WatchBadge
                  listing={this.props.listing}
                  position={this.props.position}
                  parentComponentName={this.props.componentName}
                  displayStyle="text"
                  handleLoggedOutUser={() => this.setState({ modalOpen: true })}
                />
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  clickEvent() {
    return buildListingClickEvent({
      listing: this.props.listing,
      position: this.props.position,
      listingsCount: this.props.listingsCount,
      componentName: this.props.componentName,
    });
  }

  render() {
    if (!this.props.listing) { return <PlaceholderCard />; }

    const classes = classNames(
      'listing-row-card',
      { 'listing-row-card--software': this.isDigitalListing() },
      { 'listing-row-card--discounted': this.hasDiscount() },
    );

    const { user } = this.props;
    const openNewTab = user && user.deviceName === WEB;

    return (
      <div className={classes}>
        <CoreLink
          className="listing-row-card__inner"
          to={listingItemPagePath(this.props.listing)}
          target={openNewTab ? '_blank' : undefined}
          clickEvent={this.clickEvent()}
        >
          {this.renderInner()}
        </CoreLink>
        <SignupSigninModal
          onRequestClose={() => this.setState({ modalOpen: false })}
          active={this.state.modalOpen}
          trackSource="WatchBadge"
        />
      </div>
    );
  }
}

export default withUserContext<IProps>(ListingRowCard);
